import React, { useMemo, useState } from 'react';
import { Switch } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faThLarge, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useListContext, useQueryWithStore } from 'react-admin';
import { SortSelect } from '../../Common/DataList';
import { NetOrcaTableListV1, TableListColumn } from '../../Common/NetOrcaTableList';
import { CounterpartyProvider } from '../../Teams/Counterparty';
import ServiceItemRow from './ServiceItemRow';
import ServiceItemCard from './ServiceItemCard';
import useServiceItemFilterFields from './useServiceItemFilterFields';
import { DEPENDANT_INFO, sortOptions } from './constants';
import { ServiceItemsListConfig } from './types';
import './ServiceItemsList.css';

const ServiceItemsListPage = ({ config }: { config: ServiceItemsListConfig }) => {
    // the list itself reads the records; what is needed here is the query the
    // dependant endpoint has to mirror, and the sort the panel controls
    const { currentSort, setSort, filterValues, perPage, page } = useListContext();

    const [dependant, setDependant] = useState(false);

    // Dependant items use a different endpoint but share the SAME filters,
    // sort and pagination as the owned list — only the resource changes.
    const { loading: depLoading, data: depData, total: depTotal } = useQueryWithStore(
        {
            type: 'getList',
            resource: config.dependantResource || '',
            payload: {
                pagination: { page, perPage },
                sort: { field: currentSort?.field || 'id', order: currentSort?.order || 'DESC' },
                filter: filterValues,
            },
        },
        { enabled: !!config.dependantResource && dependant },
    );

    const clickable = !dependant;
    const partyField = (dependant && config.dependantPartyField) || config.partyField;

    // Filters are declared, not hand-wired — the list loads the dropdown options
    // and owns the values. The same schema feeds the column headers, so a filter
    // is defined once and offered in whichever place is on screen.
    const filterFields = useServiceItemFilterFields(config);

    /**
     * Which filters each column header opens, in column order. The consumer-team
     * filter belongs to the party column only while that column is showing
     * consumers — dependant items list their service owner instead, so it falls
     * back to the panel.
     */
    const columns: TableListColumn[] = useMemo(() => [
        { label: 'ID', filters: ['id'], dataCy: 'column-id' },
        { label: 'Service Item', filters: ['name'], dataCy: 'column-name' },
        {
            label: partyField.label,
            icon: <FontAwesomeIcon icon={faUsers} />,
            filters: dependant ? [] : ['consumer_team_id'],
            dataCy: 'column-party',
        },
        {
            label: 'Application',
            icon: <FontAwesomeIcon icon={faThLarge} />,
            filters: ['application_id'],
            dataCy: 'column-application',
        },
        {
            label: 'Service',
            icon: <FontAwesomeIcon icon={faRocket} />,
            filters: ['service_id'],
            dataCy: 'column-service',
        },
        { label: 'State', filters: ['runtime_state', 'change_state'], dataCy: 'column-state' },
        // the created times run under this one, and the filter button takes the
        // track after it
        { label: 'Created' },
    ], [partyField.label, dependant]);

    const shared = (record: any) => ({
        record,
        // a resource that does not report change state has none to show
        showChangeState: record.change_state !== undefined,
        clickable,
        detailPath: config.detailPath,
        changeInstancesPath: config.changeInstancesPath,
        partyField,
    });

    return (
        // one panel for the page: every row names a counterparty, and only one
        // of them is ever being read
        <CounterpartyProvider>
        <NetOrcaTableListV1
            className="netorca-si"
            dataCy="service-item-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--si-cols)"
            defaultView={config.defaultView}
            renderRow={(record) => <ServiceItemRow key={record.id} {...shared(record)} />}
            renderCard={(record) => <ServiceItemCard key={record.id} {...shared(record)} />}
            drawerTitle="Filter service items"
            emptyText="No service items found"
            noun="item"
            // Dependant items are a different endpoint under the same filters,
            // and toggling starts the list over.
            source={config.dependantResource && dependant
                ? { records: depData || [], total: depTotal, loading: depLoading, key: 'dependant' }
                : undefined}
            // sorting and the dependant switch shape the list without filtering
            // it, so they sit below the fields rather than among them
            drawerFooter={
                <>
                    <div className="ft-field">
                        <label className="ft-field__label" htmlFor="si-sort">Sort</label>
                        <SortSelect id="si-sort" options={sortOptions} currentSort={currentSort} setSort={setSort} />
                    </div>

                    {config.dependantResource && (
                        <div className="ft-field">
                            <span className="ft-field__label">Dependant items</span>
                            {/* the panel has room for the explanation the toolbar
                                had to hide in a tooltip */}
                            <div className="si-dependant">
                                <Switch
                                    data-cy="dependant-switch"
                                    checked={dependant}
                                    onChange={setDependant}
                                    aria-label="Toggle dependant service items"
                                />
                                <span className="si-dependant__hint">{DEPENDANT_INFO}</span>
                            </div>
                        </div>
                    )}
                </>
            }
        />
        </CounterpartyProvider>
    );
};

export default ServiceItemsListPage;
